import { inject } from '@angular/core';
import { RedirectCommand, Router, type ActivatedRouteSnapshot, type CanActivateFn } from '@angular/router';
import { APP_ROUTES } from '@core/routes';
import { PublicContextService } from '../services/public-context.service';
import { ShareResolverService } from '../services/share-resolver.service';

export const publicContextGuard: CanActivateFn = async route => {
    const context = inject(PublicContextService);
    const resolver = inject(ShareResolverService);
    const router = inject(Router);

    const code = readShareCode(route);
    const listingId = readListingId(route);
    const current = context.shareInfo();

    context.listingId.set(listingId);

    if (!code) {
        if (current && listingId != null && current.listingIds.includes(listingId)) {
            return new RedirectCommand(
                router.createUrlTree(['/' + APP_ROUTES.PUBLIC_LISTING_DETAILS, listingId], { queryParams: { code: current.code } }),
                { state: router.getCurrentNavigation()?.extras.state }
            );
        }

        context.shareInfo.set(null);
        context.shareError.set(null);

        return true;
    }

    if (current?.code !== code) {
        const resolved = await resolver.resolve(code);

        context.shareInfo.set(resolved);
        context.shareError.set(resolved ? null : resolver.errorState());
    }

    const share = context.shareInfo();

    if (isSharedSetRoute(route) && share?.listingIds.length === 1) {
        return router.createUrlTree(['/' + APP_ROUTES.PUBLIC_LISTING_DETAILS, share.listingIds[0]], { queryParams: { code } });
    }

    if (share && listingId != null && !share.listingIds.includes(listingId)) {
        context.shareInfo.set(null);
        context.shareError.set(null);
    }

    return true;
};

function isSharedSetRoute(route: ActivatedRouteSnapshot): boolean {
    return route.paramMap.get('code') !== null;
}

function readShareCode(route: ActivatedRouteSnapshot): string | null {
    return route.paramMap.get('code') ?? route.queryParamMap.get('code');
}

function readListingId(route: ActivatedRouteSnapshot): number | null {
    const id = route.paramMap.get('id');

    return id && /^\d+$/.test(id) ? Number(id) : null;
}
